import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError, isPaywall } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { encodeWav } from "@/lib/wav";
import { speechLocale } from "@/lib/languages";

const BARS = 28;
const BURST_MS = 3500;
const BURST_LIMIT_SEC = 90;

function pickMime() {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return types.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
}

const extOf = (mime) => (mime.includes("wav") ? "wav" : mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm");
const isIOS = typeof navigator !== "undefined" && (/iPhone|iPad|iPod/i.test(navigator.userAgent)
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// MediaRecorder output when usable, otherwise a WAV built from raw mic samples (reliable on iPhone).
function currentAudio(r) {
  const mrBlob = r.chunks?.length ? new Blob(r.chunks, { type: r.mime }) : null;
  if (mrBlob && mrBlob.size >= 500) return { blob: mrBlob, mime: r.mime };
  if (r.pcm?.length) return { blob: encodeWav(r.pcm, r.rate), mime: "audio/wav" };
  return { blob: mrBlob || new Blob([]), mime: r.mime || "audio/webm" };
}
const speechLang = speechLocale;
// resume() can hang forever without a user tap; never wait on it for long.
const resumeCtx = (c) => Promise.race([c.resume?.() ?? Promise.resolve(), new Promise((r) => setTimeout(r, 400))]).catch(() => {});
const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const isMobile = typeof navigator !== "undefined" && (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
// Mobile browsers give the mic to only one consumer; browser captions would silence the recording.
export const browserCaptionsSupported = !!SR && !isMobile;

export function useDictation({ onResult, getOptions, source = "workspace", upload: customUpload }) {
  const [status, setStatus] = useState("idle");
  const [levels, setLevels] = useState(() => Array(BARS).fill(0.05));
  const [elapsed, setElapsed] = useState(0);
  const [caption, setCaption] = useState("");
  const statusRef = useRef("idle");
  const wantStop = useRef(false);
  const wantCancel = useRef(false);
  const rec = useRef({});
  const { billing } = useBilling();
  const cbs = useRef({ onResult, getOptions, customUpload, billing });
  cbs.current = { onResult, getOptions, customUpload, billing };

  const setS = (s) => { statusRef.current = s; setStatus(s); };

  const cleanup = () => {
    const r = rec.current;
    cancelAnimationFrame(r.raf);
    clearInterval(r.timer);
    clearInterval(r.burst);
    clearTimeout(r.speechCheck);
    if (r.proc) r.proc.onaudioprocess = null;
    if (r.speech) { r.speech.onend = null; try { r.speech.stop(); } catch { /* already stopped */ } }
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close().catch(() => {});
    setLevels(Array(BARS).fill(0.05));
    setElapsed(0);
  };

  const fallbackToWhisper = (lang) => {
    const r = rec.current;
    if (r.burst || statusRef.current !== "recording") return;
    if (r.speech) { r.speech.onend = null; try { r.speech.stop(); } catch { /* stopped */ } r.speech = null; }
    r.burst = startWhisperBursts(lang);
  };

  const startBrowserCaptions = (lang) => {
    const sp = new SR();
    let heard = false;
    sp.continuous = true;
    sp.interimResults = true;
    sp.lang = speechLang(lang);
    sp.onresult = (e) => {
      let t = "";
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      heard = true;
      setCaption(t);
    };
    sp.onerror = (e) => { if (!["no-speech", "aborted"].includes(e.error)) fallbackToWhisper(lang); };
    rec.current.speechCheck = setTimeout(() => { if (!heard) fallbackToWhisper(lang); }, 5000);
    sp.onend = () => { if (statusRef.current === "recording") try { sp.start(); } catch { /* restart race */ } };
    try { sp.start(); } catch { /* not allowed */ }
    return sp;
  };

  const startWhisperBursts = (lang) => setInterval(async () => {
    const r = rec.current;
    const secs = (Date.now() - r.t0) / 1000;
    if (r.inflight || statusRef.current !== "recording" || secs > BURST_LIMIT_SEC) return;
    if (secs > 30 && Date.now() - (r.lastBurst || 0) < 8000) return;
    r.inflight = true;
    r.lastBurst = Date.now();
    const fd = new FormData();
    const cur = currentAudio(r);
    fd.append("audio", cur.blob, `preview.${extOf(cur.mime)}`);
    if (lang) fd.append("language", lang);
    try {
      const { data } = await api.post("/dictation/preview", fd);
      if (statusRef.current === "recording" && data.text) setCaption(data.text);
    } catch { /* captions are best-effort */ }
    r.inflight = false;
  }, BURST_MS);

  const upload = async (blob, duration, mime) => {
    setS("processing");
    try {
      if (cbs.current.customUpload) {
        await cbs.current.customUpload(blob, `dictation.${extOf(mime)}`);
        return;
      }
      const fd = new FormData();
      fd.append("audio", blob, `dictation.${extOf(mime)}`);
      fd.append("duration_sec", String(duration));
      fd.append("source", source);
      const opts = cbs.current.getOptions?.() || {};
      ["tone", "language", "model"].forEach((k) => opts[k] && fd.append(k, opts[k]));
      if (opts.output_language && opts.output_language !== "auto") fd.append("output_language", opts.output_language);
      const { data } = await api.post("/dictation/transcribe", fd);
      if (data.cleanup_failed) toast.warning("AI polish was unavailable — showing your exact words.");
      cbs.current.onResult?.(data);
    } catch (e) {
      if (!isPaywall(e)) toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCaption("");
      setS("idle");
    }
  };

  const start = useCallback(async () => {
    if (statusRef.current !== "idle") return;
    if (cbs.current.billing && !cbs.current.billing.can_use) {
      window.dispatchEvent(new CustomEvent("mulankan:paywall", { detail: cbs.current.billing.subscribed ? "800" : "" }));
      return;
    }
    wantStop.current = false;
    wantCancel.current = false;
    setS("starting");
    const AC = window.AudioContext || window.webkitAudioContext;
    let ctx = new AC();
    ctx.resume?.().catch(() => {});
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      await resumeCtx(ctx);
      // iPhone can leave the gesture-created engine paused; a fresh one is allowed while the mic is live.
      if (ctx.state !== "running") {
        ctx.close?.().catch(() => {});
        ctx = new AC();
        await resumeCtx(ctx);
      }
      // Without a tap, the iPhone path (raw samples) can't record; ask for one.
      if (ctx.state !== "running" && (isIOS || !window.MediaRecorder)) throw new Error("gesture");
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const pcm = [];
      const proc = ctx.createScriptProcessor(4096, 1, 1);
      proc.onaudioprocess = (e) => { if (statusRef.current === "recording") pcm.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
      src.connect(proc);
      proc.connect(ctx.destination);
      let mr = null;
      const chunks = [];
      const mime = pickMime();
      if (!isIOS && window.MediaRecorder) {
        mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      }
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const t0 = Date.now();
      let last = 0;
      const tick = (ts) => {
        if (ts - last > 60) {
          last = ts;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += ((v - 128) / 128) ** 2;
          const lvl = Math.min(1, Math.sqrt(sum / buf.length) * 4 + 0.05);
          setLevels((p) => [...p.slice(1), lvl]);
        }
        rec.current.raf = requestAnimationFrame(tick);
      };
      rec.current = { stream, mr, ctx, proc, t0, chunks, pcm, rate: ctx.sampleRate, mime: mr?.mimeType || mime || "audio/webm" };
      rec.current.raf = requestAnimationFrame(tick);
      rec.current.timer = setInterval(() => setElapsed((Date.now() - t0) / 1000), 200);
      mr?.start(250);
      setCaption("");
      setS("recording");
      const opts = cbs.current.getOptions?.() || {};
      const live = opts.live_captions || "browser";
      if (live === "browser" && browserCaptionsSupported) rec.current.speech = startBrowserCaptions(opts.language);
      else if (live !== "off") rec.current.burst = startWhisperBursts(opts.language);
      if (wantStop.current) stopRef.current();
    } catch (e) {
      stream?.getTracks().forEach((t) => t.stop());
      ctx.close?.().catch(() => {});
      setS("idle");
      if (e?.message === "gesture") toast.message("Tap the mic to start listening.");
      else toast.error("Microphone access is needed. Allow it in your browser and try again.");
    }
  }, []);

  const stop = useCallback(() => {
    const r = rec.current;
    if (statusRef.current === "starting") { wantStop.current = true; return; }
    if (statusRef.current !== "recording") return;
    const duration = (Date.now() - r.t0) / 1000;
    const finish = () => {
      const { blob, mime } = currentAudio(r);
      cleanup();
      if (wantCancel.current) { setS("idle"); setCaption(""); return; }
      if (duration >= 0.6 && blob.size < 500) {
        setS("idle");
        setCaption("");
        toast.error("No audio was recorded. Check the microphone permission for this site and try again.");
        return;
      }
      if (duration < 0.6) {
        setS("idle");
        setCaption("");
        toast.message("Too short — keep talking a moment longer.");
        return;
      }
      upload(blob, duration, mime);
    };
    statusRef.current = "stopping";
    if (r.mr && r.mr.state !== "inactive") { r.mr.onstop = finish; r.mr.stop(); } else finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(() => {
    if (statusRef.current === "recording" || statusRef.current === "starting") stop();
    else start();
  }, [start, stop]);
  const cancel = useCallback(() => { wantCancel.current = true; stop(); }, [stop]);
  const stopRef = useRef(stop);
  stopRef.current = stop;

  useEffect(() => () => { if (rec.current.mr?.state === "recording") rec.current.mr.stop(); cleanup(); }, []);

  return { status, levels, elapsed, caption, start, stop, toggle, cancel, statusRef };
}
